/**
 * 전문가 AI 종합 분석 리포트 패널 (원샷 — 대화 없음)
 * 버튼 클릭 → 전문가 정보 종합 분석 리포트 표시 (캐시 우선)
 */
import React, { useState } from 'react';
import { Bot, X, Sparkles } from 'lucide-react';
import { apiBaseUrl } from '../config/runtime';
import { useTranslation } from 'react-i18next';

interface Props {
  expertId: number;
  expertNickname: string;
}

const INDIGO = '#4F46E5';

const ExpertAnalysisChatbot: React.FC<Props> = ({ expertId, expertNickname }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [report, setReport] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchAnalysis = async () => {
    console.log('[ExpertAnalysis] 분석 요청 — 전문가:', expertId);
    setOpen(true);
    if (report || loading) return; // 이미 로드됨
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(apiBaseUrl('/chatbot/expert-analysis'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ expertId, lang: i18n.language.startsWith('ko') ? 'ko' : 'en' }),
      });
      const data = await res.json();
      console.log('[ExpertAnalysis] 응답 — success:', data.success, 'cached:', data.data?.cached);

      if (data.success) {
        setReport(data.data.reply);
      } else {
        setError(data.message || t('aiChat.requestFailed'));
      }
    } catch (e) {
      console.error('[ExpertAnalysis] 오류:', e);
      setError(t('aiChat.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={fetchAnalysis}
        data-testid="analysis-open-btn"
        style={{
          width: '100%', padding: '11px', marginTop: 10,
          background: 'rgba(79,70,229,0.08)', color: INDIGO,
          border: '1px solid rgba(79,70,229,0.3)', borderRadius: 10,
          fontSize: 14, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          fontFamily: 'inherit',
        }}
      >
        <Sparkles size={14} />{t('aiChat.btn')}
      </button>
    );
  }

  return (
    <div style={{
      marginTop: 12, border: '1px solid #E5E7EB', borderRadius: 12,
      background: '#fff', display: 'flex', flexDirection: 'column',
      maxHeight: 480, overflow: 'hidden',
    }}>
      {/* 헤더 */}
      <div style={{
        padding: '10px 14px', background: INDIGO, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Bot size={16} />
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>{expertNickname} — {t('aiChat.title')}</span>
        <X size={16} style={{ cursor: 'pointer' }} onClick={() => { console.log('[ExpertAnalysis] 닫기'); setOpen(false); }} />
      </div>

      {/* 리포트 영역 */}
      <div style={{ overflowY: 'auto', padding: 16, background: '#FAFAFF' }}>
        {loading && (
          <div style={{ fontSize: 13, color: '#999', padding: '20px 0', textAlign: 'center' }}>
            {t('aiChat.analyzing')}
          </div>
        )}
        {error && (
          <div style={{ fontSize: 13, color: '#DC2626', padding: '12px 0' }}>{error}</div>
        )}
        {report && (
          <div style={{
            fontSize: 13, lineHeight: 1.7, color: '#333',
            whiteSpace: 'pre-wrap',
          }}>
            {report}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpertAnalysisChatbot;
